import { useAuth } from "@/context/AuthContext";
import ManagerConsole from "@/pages/ManagerConsole";
import AttorneyPortal from "@/pages/AttorneyPortal";
import { Inbox, Scale, UserCheck, Users, UserPlus, XCircle, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import TrackerTable from "@/components/TrackerTable";

const TAB_DEFS = {
  attorney:        { key: "attorney",  label: "Attorney View", icon: Scale },
  manager:         { key: "manager",   label: "Manager View",  icon: UserCheck },
  analyst:         { key: "analyst",   label: "My Assigned",   icon: Inbox },
  team:            { key: "team",      label: "Team View",     icon: Users },
};

function tabsFor(role) {
  if (role === "attorney") return ["attorney", "analyst"];
  if (role === "legal_manager" || role === "general_counsel") return ["manager", "attorney", "team", "analyst"];
  if (role === "it_admin") return ["team", "manager", "attorney", "analyst"];
  return ["analyst"];
}

export default function MyQueue() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => tabsFor(user?.role)[0]);
  const [summary, setSummary] = useState(null);

  const loadSummary = () => {
    api.get("/queue/summary").then(r => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { loadSummary(); }, []);

  const available = tabsFor(user?.role);

  return (
    <div className="p-6" data-testid="my-queue-page">
      <div className="flex items-start gap-4 mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Inbox className="w-3.5 h-3.5" /> Legal Department
          </div>
          <h1 className="text-2xl font-bold text-slate-900">My Queue</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every contract currently assigned to you. Switch tabs to preview how the same queue looks from another Legal role.
          </p>
        </div>
        <div className="ml-auto"><DelegationCard summary={summary} onChanged={loadSummary} /></div>
      </div>

      {available.length > 1 && (
        <div className="border-b border-slate-200 mb-4 flex gap-1 flex-wrap">
          {available.map(k => {
            const t = TAB_DEFS[k]; const Icon = t.icon; const active = tab === k;
            return (
              <button key={k} onClick={() => setTab(k)}
                      className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 ${active ? "border-[#005289] text-[#005289]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                      data-testid={`queue-tab-${k}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
      )}

      {tab === "attorney" && <AttorneyPortal />}
      {tab === "manager" && <ManagerConsole />}
      {tab === "team" && <TeamView />}
      {tab === "analyst" && <AnalystQueue user={user} nav={nav} summary={summary} />}
    </div>
  );
}

function AnalystQueue({ user, nav, summary }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const ids = [user?.id, ...((summary?.covering_for || []).map(u => u.id))];
    Promise.all(ids.filter(Boolean).map(id => api.get(`/contracts?assigned_to=${id}`).then(r => r.data))).then(lists => {
      const merged = []; const seen = new Set();
      lists.flat().forEach(c => { if (!seen.has(c.id)) { seen.add(c.id); merged.push(c); } });
      setRows(merged);
    }).catch(() => setRows([]));
  }, [user?.id, summary]);
  return rows === null ? <div className="text-sm text-slate-500">Loading…</div>
    : rows.length === 0 ? <div className="bg-white border border-dashed border-slate-200 rounded-md p-8 text-center text-slate-500 text-sm">No contracts assigned to you.</div>
    : <TrackerTable rows={rows} onChanged={() => nav(0)} testIdPrefix="my-queue-tt" />;
}

function TeamView() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/queue/team-view").then(r => setData(r.data)).catch(() => setData({rows: []})); }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="queue-team-view">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="text-left px-4 py-2">Reviewer</th><th className="text-left px-4 py-2">Role</th><th className="num px-4 py-2">Assigned</th><th className="num px-4 py-2">Overdue</th><th className="text-left px-4 py-2">Delegated</th></tr>
        </thead>
        <tbody>
          {data.rows.map(r => (
            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`team-row-${r.id}`}
                onClick={() => nav(`/pipeline?search=${encodeURIComponent(r.email)}`)}>
              <td className="px-4 py-2 font-semibold">{r.name}</td>
              <td className="px-4 py-2 text-xs">{(r.role || "").replace(/_/g, " ")}</td>
              <td className="num px-4 py-2 font-bold">{r.count}</td>
              <td className="num px-4 py-2">{r.overdue ? <span className="text-red-600 font-bold">{r.overdue}</span> : "—"}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{r.delegation ? `→ ${r.delegation.delegating_to} (${new Date(r.delegation.until).toLocaleDateString()})` : "—"}</td>
            </tr>
          ))}
          {data.rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6 text-sm">No Legal users yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function DelegationCard({ summary, onChanged }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [toUser, setToUser] = useState("");
  const [until, setUntil] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/users?role_group=legal").then(r => setUsers(r.data || []))
      .catch(() => api.get("/users").then(r => setUsers(r.data || [])).catch(() => {}));
  }, [open]);
  const submit = async () => {
    if (!toUser || !until) return toast.error("Pick a person and expiry");
    try {
      const iso = new Date(until).toISOString();
      await api.post("/queue/delegate", { to_user_id: toUser, until_iso: iso });
      toast.success("Delegation set"); setOpen(false); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const clear = async () => {
    try { await api.post("/queue/delegate", { to_user_id: null }); toast.success("Delegation cleared"); onChanged && onChanged(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 min-w-[260px]" data-testid="delegation-card">
      {summary?.covering_for?.length > 0 && (
        <div className="text-[11px] text-emerald-700 font-semibold mb-1 flex items-center gap-1">
          <Users className="w-3 h-3" /> Covering for {summary.covering_for.map(c => c.name).join(", ")}
        </div>
      )}
      {summary?.delegate ? (
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-amber-600" />
          <div className="text-xs flex-1">
            <div>Out — <b>{summary.delegate.name}</b> covers your queue</div>
            <div className="text-[10px] text-slate-400">until {new Date(summary.delegate.until).toLocaleString()}</div>
          </div>
          <button onClick={clear} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50" data-testid="delegation-clear-btn"><XCircle className="w-3 h-3" /></button>
        </div>
      ) : open ? (
        <div className="space-y-2">
          <select value={toUser} onChange={e => setToUser(e.target.value)} className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid="delegation-user-select">
            <option value="">— Pick a colleague —</option>
            {users.filter(u => ["contract_analyst","attorney","legal_manager","general_counsel","records_management"].includes(u.role))
                  .map(u => <option key={u.id} value={u.id}>{u.name} · {u.role.replace(/_/g," ")}</option>)}
          </select>
          <input type="datetime-local" value={until} onChange={e => setUntil(e.target.value)} className="w-full h-8 px-2 text-xs border border-slate-200 rounded" data-testid="delegation-until" />
          <div className="flex gap-1.5">
            <button onClick={submit} className="flex-1 h-7 bg-[#005289] text-white text-xs rounded font-semibold" data-testid="delegation-save-btn">Delegate</button>
            <button onClick={() => setOpen(false)} className="h-7 px-2 text-xs border border-slate-200 rounded">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="w-full text-xs font-semibold text-[#005289] flex items-center justify-center gap-1.5 py-1.5 hover:bg-slate-50 rounded" data-testid="delegation-open-btn">
          <UserPlus className="w-3 h-3" /> Delegate my queue
        </button>
      )}
    </div>
  );
}
